import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@core/supabase/client';
import { queryKeys } from '@core/query/queryKeys';

type Options = {
  debounceMs?: number;
  /** Fires when a debounced remote UPDATE was detected (before cache invalidation). */
  onRemoteChange?: () => void;
};

/**
 * Supabase Realtime: when another session updates `ggj_b2b_applications` for this org, invalidate
 * React Query application lists (PRD §10.2 — visibility of concurrent edits).
 * Requires Realtime enabled for `ggj_b2b_applications` in the Supabase project.
 */
export function useApplicationsOrgRealtime(
  orgId: string | undefined,
  queryClient: QueryClient,
  options?: Options,
): void {
  const debounceMs = options?.debounceMs ?? 450;
  const onRemoteChange = options?.onRemoteChange;
  const onRemoteRef = useRef(onRemoteChange);
  onRemoteRef.current = onRemoteChange;

  useEffect(() => {
    if (!orgId) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const scheduleInvalidate = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        onRemoteRef.current?.();
        void queryClient.invalidateQueries({ queryKey: queryKeys.applications.all(orgId) });
      }, debounceMs);
    };

    const channel = supabase
      .channel(`realtime:ggj_b2b_applications:${orgId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'ggj_b2b_applications',
          filter: `org_id=eq.${orgId}`,
        },
        () => scheduleInvalidate(),
      )
      .subscribe();

    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [orgId, queryClient, debounceMs]);
}
